import { ChevronDown, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useUpdateCaseStatus } from '@/hooks/use-case-queries'
import type { CaseData } from '@/pages/cases/CaseContext'

type CaseStatus = CaseData['status']
const STATUS_OPTIONS: CaseStatus[] = ['draft', 'active', 'closed', 'archived']
const STATUS_VARIANTS = {
  draft: 'muted',
  active: 'success',
  closed: 'muted',
  archived: 'warning',
} as const

export function CaseStatusPicker({ caseId, status }: { caseId: string; status: CaseStatus }) {
  const { t } = useTranslation()
  const update = useUpdateCaseStatus(caseId)
  const changeStatus = (next: CaseStatus) => {
    if (next === status) return
    update.mutate(next, {
      onSuccess: () =>
        toast.success(t('overview.statusChanged', { status: t(`cases.status.${next}`) })),
    })
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" disabled={update.isPending}>
          <Badge variant={STATUS_VARIANTS[status]}>{t(`cases.status.${status}`)}</Badge>
          {update.isPending ? <Loader2 className="size-3 animate-spin" /> : <ChevronDown />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {STATUS_OPTIONS.map(option => (
          <DropdownMenuItem key={option} onSelect={() => changeStatus(option)}>
            <Badge variant={STATUS_VARIANTS[option]}>{t(`cases.status.${option}`)}</Badge>
            {option === status && (
              <span className="ml-auto text-muted-foreground">{t('common.current')}</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
